import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaChevronDown, FaEye, FaEyeSlash, FaMobileAlt, FaKey } from 'react-icons/fa';
import Toast from '../components/Toast';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import * as authService from '../services/authService';
import * as profileService from '../services/profileService';

const RESEND_COOLDOWN = 30; // seconds

const Login = () => {
  const navigate = useNavigate();

  /* ─── Shared state ─── */
  const [loginMode, setLoginMode] = useState('password'); // 'password' | 'otp'
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  /* ─── Shared Form State ─── */
  const [formData, setFormData] = useState({
    userType: 'young-india', // young-india, professor
    role_id: '',
    username: '', // email or phone
    password: '',
    remember_me: true,
  });

  /* ─── OTP-specific state ─── */
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const timerRef = useRef(null);

  const [showPassword, setShowPassword] = useState(false);

  /* ─── Load roles ─── */
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true);
        const rolesData = await authService.fetchRoles();
        const relevantRoles = rolesData
          .filter(role => ['graduate', 'post-graduate', 'phd', 'professor', 'reviewer', 'editorial'].includes(role.code))
          .map(role => ({
            ...role,
            description: role.code === 'professor' ? 'Professor' : (role.code === 'reviewer' ? 'Reviewer' : (role.code === 'editorial' ? 'EB Member' : role.description))
          }));
        setRoles(relevantRoles);

        const graduateRole = relevantRoles.find(r => r.code === 'graduate');
        if (graduateRole) {
          setFormData(prev => ({ ...prev, role_id: graduateRole.id }));
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        const fallbackRoles = [
          { id: '1', description: 'Graduate', code: 'graduate' },
          { id: '2', description: 'Post Graduate', code: 'post-graduate' },
          { id: '3', description: 'PhD Scholar', code: 'phd' },
          { id: '4', description: 'Professor', code: 'professor' },
          { id: '5', description: 'Reviewer', code: 'reviewer' },
          { id: '6', description: 'EB Member', code: 'editorial' },
        ];
        setRoles(fallbackRoles);
        setFormData(prev => ({ ...prev, role_id: fallbackRoles[0].id }));
      } finally {
        setLoadingRoles(false);
      }
    };
    loadRoles();
  }, []);

  /* ─── Role syncing ─── */
  useEffect(() => {
    if (roles.length === 0) return;
    let targetRole;
    if (formData.userType === 'young-india') {
      targetRole = roles.find(r => r.code === 'graduate');
    } else if (formData.userType === 'professor') {
      // Default to researcher (phd) for professor category if role_id not set to a professor category role
      const currentRole = roles.find(r => r.id === formData.role_id);
      const isProfessorType = currentRole && ['phd', 'professor', 'reviewer', 'editorial'].includes(currentRole.code);
      if (!isProfessorType) {
        targetRole = roles.find(r => r.code === 'phd');
      }
    }

    if (targetRole && formData.role_id !== targetRole.id) {
      setFormData(prev => ({ ...prev, role_id: targetRole.id }));
    }
  }, [formData.userType, roles]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  /* ─── Helpers ─── */
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const startResendTimer = () => {
    setResendCooldown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const finalizeLogin = async (response, roleId, rememberMe) => {
    authService.clearSession();
    const { data } = response;
    const userData = {
      id: data.user_id,
      role: data.role_code,
      role_label: data.role_label,
      is_reviewer: data.permissions.is_reviewer,
      is_editorial_board: data.permissions.is_editorial_board,
      is_verified_sub_eb: data.permissions.is_verified_sub_eb,
      payment_status: data.payment_status || 'PAID',
      loginTime: new Date().toISOString(),
    };

    authService.storeSession(userData, null, null, rememberMe);

    try {
      const profileResponse = await profileService.getProfile();
      if (profileResponse?.success && profileResponse.data) {
        const pData = profileResponse.data;
        const personal = pData.personal || {};
        const fullName = pData.full_name || personal.full_name || pData.name || personal.name ||
          `${personal.first_name || ''} ${personal.last_name || ''}`.trim();
        if (fullName) {
          userData.full_name = fullName;
          userData.name = fullName;
        }
        if (pData.payment_status) userData.payment_status = pData.payment_status;
        authService.storeSession(userData, null, null, rememberMe);
      }
    } catch (profileError) {
      console.error('Error fetching profile after login:', profileError);
    }

    window.dispatchEvent(new Event('userLogin'));
    showToast('Login successful', 'success');
    const redirectPath = userData.payment_status === 'PENDING' ? '/?showPayment=1' : '/';
    setTimeout(() => navigate(redirectPath), 800);
  };

  /* ─── Input Handlers ─── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'userType') {
      let newRoleId = formData.role_id;
      if (value === 'young-india') {
        const grad = roles.find(r => r.code === 'graduate');
        newRoleId = grad ? grad.id : roles[0]?.id;
      } else if (value === 'professor') {
        const prof = roles.find(r => r.code === 'professor');
        newRoleId = prof ? prof.id : roles[0]?.id;
      }
      setFormData({ ...formData, userType: value, role_id: newRoleId });
    } else {
      let finalValue = type === 'checkbox' ? checked : value;
      if (name === 'username' && /^\d+$/.test(finalValue) && finalValue.length > 10) {
        finalValue = finalValue.slice(0, 10);
      }
      setFormData({ ...formData, [name]: finalValue });
    }
  };

  /* ─── Submission Handlers ─── */
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const loginData = {
        role_id: formData.role_id,
        username: formData.username,
        password: formData.password,
        remember_me: formData.remember_me
      };
      const response = await authService.loginUser(loginData);
      if (response.success) {
        await finalizeLogin(response, formData.role_id, formData.remember_me);
      } else {
        showToast(response.message || 'Login failed', 'error');
      }
    } catch (error) {
      showToast(error.message || 'An error occurred during login.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const phone = formData.username;
    if (!/^\d{10}$/.test(phone)) {
      showToast('Please enter a valid 10-digit phone number in the email/phone field.', 'error');
      return;
    }
    if (!formData.role_id) {
      showToast('Please select your role first.', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const response = await authService.sendLoginOtp(phone, formData.role_id);
      if (response.success) {
        setOtpSent(true);
        startResendTimer();
        showToast('OTP sent successfully!', 'success');
      } else {
        showToast(response.message || 'Failed to send OTP', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Error sending OTP', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      showToast('Please enter the 6-digit OTP.', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const response = await authService.verifyLoginOtp(
        formData.username,
        formData.role_id,
        otp,
        formData.remember_me
      );
      if (response.success) {
        await finalizeLogin(response, formData.role_id, formData.remember_me);
      } else {
        showToast(response.message || 'OTP verification failed', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Invalid or expired OTP', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (mode) => {
    setLoginMode(mode);
    setOtpSent(false);
    setOtp('');
    setResendCooldown(0);
    clearInterval(timerRef.current);
  };

  return (
    <>
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="min-h-screen flex">
        {/* Left Side - Image */}
        <div className="hidden lg:block lg:w-1/2 fixed left-0 top-0 h-screen">
          <div className="relative w-full h-full">
            <div className="absolute inset-0 bg-black/50 z-10"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/60 z-10"></div>
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            >
              <source src="/istockphoto-1542748041-640_adpp_is.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white p-12">
              <img src="/logo.png" alt="Paper Portal" className="h-20 mb-8" />
              <h2 className="text-4xl font-bold mb-4 text-center">Welcome Back!</h2>
              <p className="text-xl text-center max-w-md">
                Access your research dashboard and continue your academic journey.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold">1K+</div>
                  <div className="text-sm">Papers</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold">200+</div>
                  <div className="text-sm">Researchers</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 lg:ml-[50%] min-h-screen flex items-center justify-center bg-gradient-to-br from-light-blue to-white p-4 sm:p-8">
          <div className="max-w-3xl w-full">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <img src="/logo.png" alt="Paper Portal" className="h-16 mx-auto mb-4" />
            </div>

            <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-10">
              <div className="text-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Login</h2>
                <p className="text-gray-600 mt-2">Enter your credentials to continue</p>
              </div>

              {/* ── Mode toggle ── */}
              <div className="flex rounded-xl overflow-hidden border-2 border-primary-blue/10 mb-8">
                <button
                  type="button"
                  onClick={() => switchMode('password')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs sm:text-sm font-semibold transition-all ${loginMode === 'password'
                    ? 'bg-gradient-to-r from-primary-blue to-secondary-blue text-white shadow-md'
                    : 'bg-white text-gray-500 hover:bg-blue-50'
                    }`}
                >
                  <FaLock className="text-xs" />
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => switchMode('otp')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs sm:text-sm font-semibold transition-all ${loginMode === 'otp'
                    ? 'bg-gradient-to-r from-primary-blue to-secondary-blue text-white shadow-md'
                    : 'bg-white text-gray-500 hover:bg-blue-50'
                    }`}
                >
                  <FaMobileAlt className="text-xs" />
                  OTP
                </button>
              </div>

              <form onSubmit={loginMode === 'password' ? handlePasswordSubmit : handleOtpSubmit} className="space-y-6">
                {/* Role Selection - Restored exactly as before */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Login As *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Young India */}
                    <label
                      className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.userType === 'young-india'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:bg-blue-50 hover:border-primary-blue'
                        }`}
                    >
                      <input
                        type="radio"
                        name="userType"
                        value="young-india"
                        checked={formData.userType === 'young-india'}
                        onChange={handleChange}
                        disabled={isLoading || loadingRoles}
                        className="mb-2 h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 cursor-pointer disabled:opacity-50"
                      />
                      <div className="text-center">
                        <div className="font-bold text-gray-900 text-sm mb-1">Young India</div>
                        <div className="text-xs text-gray-600">Graduate & Post Graduate</div>
                      </div>
                    </label>

                    {/* Professor / Researcher / Reviewer */}
                    <label
                      className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.userType === 'professor'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-blue-50 hover:border-primary-blue'
                        }`}
                    >
                      <input
                        type="radio"
                        name="userType"
                        value="professor"
                        checked={formData.userType === 'professor'}
                        onChange={handleChange}
                        disabled={isLoading || loadingRoles}
                        className="mb-2 h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300 cursor-pointer disabled:opacity-50"
                      />
                      <div className="text-center">
                        <div className="font-bold text-gray-900 text-sm mb-1">Researcher / Professor / Reviewer</div>
                        <div className="text-xs text-gray-600">PhD Scholars, Professors, Reviewers</div>
                      </div>
                    </label>
                  </div>

                  {/* Sub-selection for Academic category */}
                  {formData.userType === 'professor' && (
                    <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg animate-fade-in">
                      <label htmlFor="role_id_academic" className="block text-sm font-medium text-gray-700 mb-2">
                        Select Your Specific Role *
                      </label>
                      <div className="relative">
                        <select
                          id="role_id_academic"
                          name="role_id"
                          value={formData.role_id}
                          onChange={handleChange}
                          required
                          disabled={isLoading || loadingRoles}
                          className="w-full px-4 pr-10 py-2.5 sm:py-3 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all disabled:opacity-50 appearance-none text-[13px] sm:text-sm"
                        >
                          <option value="">Choose your role</option>
                          {[
                            { id: 'phd', label: 'Researcher (Ongoing-PhD / Assistant Professor)' },
                            { id: 'professor', label: 'Professor' },
                            { id: 'reviewer', label: 'Reviewer' },
                            { id: 'editorial', label: 'EB Member' }
                          ].map((roleOption) => {
                            const targetCode = roleOption.id === 'editorial' ? (roles.some(r => r.code === 'editorial') ? 'editorial' : 'professor') : roleOption.id;
                            const roleObj = roles.find(r => r.code === targetCode);
                            if (!roleObj) return null;
                            return (
                              <option key={roleOption.id} value={roleObj.id}>
                                {roleOption.label}
                              </option>
                            );
                          })}
                        </select>
                        <FaChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-500 pointer-events-none" />
                      </div>
                    </div>
                  )}

                  {/* Sub-selection for Young India */}
                  {formData.userType === 'young-india' && (
                    <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg animate-fade-in">
                      <label htmlFor="role_id_yi" className="block text-sm font-medium text-gray-700 mb-2">
                        Select Your Education Level *
                      </label>
                      <div className="relative">
                        <select
                          id="role_id_yi"
                          name="role_id"
                          value={formData.role_id}
                          onChange={handleChange}
                          required
                          disabled={isLoading || loadingRoles}
                          className="w-full px-4 pr-10 py-2.5 sm:py-3 border-2 border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all disabled:opacity-50 appearance-none text-[13px] sm:text-sm"
                        >
                          {roles.filter(r => r.code === 'graduate' || r.code === 'post-graduate').map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.description}
                            </option>
                          ))}
                        </select>
                        <FaChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-green-500 pointer-events-none" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Username Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{loginMode === 'otp' ? 'Phone Number *' : 'Email / Phone Number *'}</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {loginMode === 'otp' ? <FaMobileAlt className="text-gray-400" /> : <FaUser className="text-gray-400" />}
                      </div>
                      <input
                        type={loginMode === 'otp' ? 'tel' : 'text'}
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        required
                        disabled={isLoading || (loginMode === 'otp' && otpSent)}
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue transition-all"
                        placeholder={loginMode === 'otp' ? '10-digit phone number' : 'Enter email or phone'}
                      />
                    </div>
                    {loginMode === 'otp' && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isLoading || resendCooldown > 0}
                        className="px-4 py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white text-sm font-semibold rounded-lg disabled:opacity-50 whitespace-nowrap"
                      >
                        {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : otpSent ? 'Resend' : 'Send OTP'}
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-blue-600 font-medium">
                    💡 Use your registered email address or phone number
                  </p>
                </div>

                {/* Password or OTP Input */}
                {loginMode === 'password' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FaLock className="text-gray-400" /></div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue"
                        placeholder="Enter your password"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary-blue">
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                ) : otpSent && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Enter 6-Digit OTP *</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FaKey className="text-gray-400" /></div>
                      <input
                        type="text"
                        value={otp}
                        onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue tracking-[0.4em] text-center font-bold text-lg"
                        placeholder="••••••"
                        autoFocus
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="rem_me"
                      name="remember_me"
                      type="checkbox"
                      checked={formData.remember_me}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-blue border-gray-300 rounded"
                    />
                    <label htmlFor="rem_me" className="ml-2 block text-sm text-gray-700">Remember me</label>
                  </div>
                  {loginMode === 'password' && (
                    <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm font-medium text-primary-blue hover:underline">Forgot password?</button>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || (loginMode === 'otp' && !otpSent)}
                  className="w-full py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white font-bold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-[1.01] transition-all disabled:opacity-50"
                >
                  {isLoading ? 'Processing...' : loginMode === 'otp' ? 'Login with OTP' : 'Login to Account'}
                </button>

                {/* Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Don't have an account? Apply as</span>
                  </div>
                </div>

                {/* Registration Links in Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link
                    to="/signup?type=young-india"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    <FaUser className="w-4 h-4" />
                    <span>Young India</span>
                  </Link>

                  <Link
                    to="/signup?type=professor"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    <FaUser className="w-4 h-4" />
                    <span className="text-xs lg:text-sm">Researcher / Professor / Reviewer / Board</span>
                  </Link>
                </div>
              </form>
            </div>

            {/* Features in Single Line */}
            <div className="mt-8 grid grid-cols-3 gap-0 text-center border-t border-gray-100 pt-8">
              <div className="border-r border-gray-100">
                <div className="text-primary-blue font-bold text-lg sm:text-2xl">Secure</div>
                <div className="text-gray-500 text-[10px] sm:text-sm uppercase tracking-wider">Protected</div>
              </div>
              <div className="border-r border-gray-100">
                <div className="text-primary-blue font-bold text-lg sm:text-2xl">Fast</div>
                <div className="text-gray-500 text-[10px] sm:text-sm uppercase tracking-wider">Access</div>
              </div>
              <div>
                <div className="text-primary-blue font-bold text-lg sm:text-2xl">24/7</div>
                <div className="text-gray-500 text-[10px] sm:text-sm uppercase tracking-wider">Support</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;